import { useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { BaseButton } from '@repo/common';

import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';
import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/20px/icon_20px_info_filled.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/16px/icon_16px_plus_outline.svg';
import { ReactComponent as SearchIcon } from '@repo/common/assets/icons/20px/icon_20px_search.svg';

const normalizeOptions = (options) =>
  options.map((option, index) =>
    typeof option === 'string'
      ? { value: option, label: option }
      : { value: option?.value ?? String(index), label: option?.label ?? '' },
  );

const normalizeLabel = (label = '') => label.trim().toLowerCase();

const uniqueOptions = (options) => {
  const seen = new Set();
  return options.filter((option) => {
    if (!option?.label || seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
};

const normalizeSelectedOptionLabels = (selectedOptionLabels = {}) => {
  if (selectedOptionLabels instanceof Map) return selectedOptionLabels;
  return new Map(Object.entries(selectedOptionLabels || {}));
};

function SearchChip({ label, selected = false, onClick }) {
  const Icon = selected ? CancelIcon : PlusIcon;

  return (
    <BaseButton
      type='text'
      size='m'
      htmlType='button'
      typo={false}
      isStatic
      gap='gap-2'
      iconPosition='after'
      icon={<Icon className='size-4 shrink-0' aria-hidden />}
      onClick={onClick}
      textWrapperClass='h-auto min-w-0 max-w-[calc(100vw-96px)] flex-none truncate whitespace-nowrap typo-b12Strong'
      className={twMerge(
        'h-[34px] max-w-full min-w-0 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-[100px] border-0 py-2 pl-3 pr-2 transition-colors',
        selected
          ? 'bg-grayscale-gray07 text-grayscale-gray01'
          : 'bg-grayscale-gray01 text-grayscale-gray07 hover:bg-grayscale-gray02',
      )}
    >
      {label}
    </BaseButton>
  );
}

function SelectedInlineValue({ option, onRemove }) {
  if (!option) return null;

  return (
    <div className='flex min-h-6 w-full flex-wrap items-center gap-1'>
      <span className='typo-b13 text-text-placeholder'>Selected:</span>
      <BaseButton
        type='text'
        size='m'
        htmlType='button'
        typo={false}
        isStatic
        gap='gap-1'
        iconPosition='after'
        icon={<CancelIcon className='size-4 shrink-0' aria-hidden />}
        onClick={onRemove}
        textWrapperClass='h-auto min-w-0 flex-none truncate'
        className='h-auto min-w-0 cursor-pointer items-center gap-1 rounded-none border-0 bg-transparent p-0 text-left typo-b14 text-text-primary'
      >
        {option.label}
      </BaseButton>
    </div>
  );
}

function SelectedInlineValues({ options = [], onRemove }) {
  if (options.length === 0) return null;

  return (
    <div className='flex min-h-6 w-full flex-wrap items-center gap-x-1 gap-y-1'>
      <span className='typo-b13 text-text-placeholder'>Selected:</span>
      {options.map((option) => (
        <BaseButton
          key={option.value}
          type='text'
          size='m'
          htmlType='button'
          typo={false}
          isStatic
          gap='gap-1'
          iconPosition='after'
          icon={<CancelIcon className='size-4 shrink-0' aria-hidden />}
          onClick={() => onRemove(option)}
          textWrapperClass='h-auto min-w-0 max-w-[calc(100vw-96px)] flex-none truncate whitespace-nowrap'
          className='h-auto max-w-full min-w-0 cursor-pointer items-center gap-1 rounded-none border-0 bg-transparent p-0 text-left typo-b14 text-text-primary'
        >
          {option.label}
        </BaseButton>
      ))}
    </div>
  );
}

function PathfinderMobileSearchSelect({
  options = [],
  values,
  defaultValues = [],
  onChange,
  query,
  defaultQuery = '',
  onQueryChange,
  loadOptions,
  placeholder = 'Search',
  allowCreate = true,
  maxSelections = Infinity,
  minQueryLength = 2,
  createLabel = (nextQuery) => `Add '${nextQuery}'`,
  selectedOptionLabels = {},
  defaultSuggestionTitle = '',
  showDefaultSuggestions = false,
  limitMessage = (count) => `You can select up to ${count} options.`,
  className = '',
}) {
  const [internalValues, setInternalValues] = useState(defaultValues);
  const [internalQuery, setInternalQuery] = useState(defaultQuery);
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const [customOptions, setCustomOptions] = useState([]);

  const selectedValues = values === undefined ? internalValues : values;
  const currentQuery = query === undefined ? internalQuery : query;
  const isSingleSelection = maxSelections === 1;
  const normalizedQuery = normalizeLabel(currentQuery);
  const selectedLabelMap = useMemo(
    () => normalizeSelectedOptionLabels(selectedOptionLabels),
    [selectedOptionLabels],
  );

  useEffect(() => {
    if (!loadOptions || normalizedQuery.length < minQueryLength) {
      setAsyncOptions([]);
      return undefined;
    }

    let alive = true;
    const timer = setTimeout(async () => {
      try {
        const loadedOptions = await loadOptions(currentQuery.trim());
        if (alive) setAsyncOptions(normalizeOptions(loadedOptions || []));
      } catch {
        if (alive) setAsyncOptions([]);
      }
    }, 250);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [currentQuery, loadOptions, minQueryLength, normalizedQuery]);

  const normalizedOptions = useMemo(
    () =>
      uniqueOptions([
        ...normalizeOptions(options),
        ...asyncOptions,
        ...customOptions,
        ...selectedValues.map((value) => ({
          value,
          label: selectedLabelMap.get(value) ?? String(value),
        })),
      ]),
    [asyncOptions, customOptions, options, selectedLabelMap, selectedValues],
  );
  const selectedValueSet = useMemo(
    () => new Set(selectedValues),
    [selectedValues],
  );
  const selectedOptions = normalizedOptions.filter((option) =>
    selectedValueSet.has(option.value),
  );
  const suggestions = normalizedOptions
    .filter(
      (option) =>
        (isSingleSelection || !selectedValueSet.has(option.value)) &&
        (!normalizedQuery ||
          normalizeLabel(option.label).includes(normalizedQuery)),
    )
    .slice(0, 8);
  const canCreate =
    allowCreate &&
    Boolean(normalizedQuery) &&
    !normalizedOptions.some(
      (option) => normalizeLabel(option.label) === normalizedQuery,
    );
  const showSuggestionPanel = isOpen && (suggestions.length > 0 || canCreate);
  const showDefaultSuggestionPanel =
    showDefaultSuggestions &&
    selectedOptions.length === 0 &&
    normalizedQuery.length === 0 &&
    normalizedOptions.length > 0;

  const emitValues = (nextValues, extraOptions = []) => {
    const optionPool = uniqueOptions([...normalizedOptions, ...extraOptions]);
    if (values === undefined) setInternalValues(nextValues);
    onChange?.(
      nextValues,
      optionPool.filter((option) => nextValues.includes(option.value)),
    );
  };

  const setQuery = (nextQuery) => {
    if (query === undefined) setInternalQuery(nextQuery);
    onQueryChange?.(nextQuery);
  };

  const addOption = (option) => {
    if (selectedValueSet.has(option.value)) return;
    setCustomOptions((previous) =>
      previous.some((item) => item.value === option.value)
        ? previous
        : [...previous, option],
    );
    if (isSingleSelection) {
      emitValues([option.value], [option]);
      setShowLimitMessage(false);
      return;
    }
    if (selectedValues.length >= maxSelections) {
      setShowLimitMessage(true);
      return;
    }
    emitValues([...selectedValues, option.value], [option]);
    setShowLimitMessage(false);
  };

  const removeOption = (option) => {
    emitValues(selectedValues.filter((value) => value !== option.value));
    setShowLimitMessage(false);
  };

  const createOption = () => {
    const label = currentQuery.trim();
    if (!label) return;
    const option = {
      value: `custom-${normalizeLabel(label).replace(/\s+/g, '-')}`,
      label,
    };
    setCustomOptions((previous) =>
      previous.some((item) => item.value === option.value)
        ? previous
        : [...previous, option],
    );
    addOption(option);
    setQuery('');
  };

  return (
    <div className={twMerge('flex w-full flex-col gap-8', className)}>
      {isSingleSelection && selectedOptions.length > 0 ? (
        <SelectedInlineValue
          option={selectedOptions[0]}
          onRemove={() => removeOption(selectedOptions[0])}
        />
      ) : null}

      {!isSingleSelection && selectedOptions.length > 0 ? (
        <SelectedInlineValues
          options={selectedOptions}
          onRemove={removeOption}
        />
      ) : null}

      <div className='flex w-full flex-col gap-1'>
        <div
          className={twMerge(
            'flex h-12 w-full items-center gap-[6px] rounded-[15px] border-[1.6px] bg-surface-lightgray60 px-3 py-1 transition-colors',
            isOpen ? 'border-surface-green40' : 'border-transparent',
          )}
        >
          <SearchIcon
            className={twMerge(
              'size-5 shrink-0',
              isOpen ? 'text-text-success' : 'text-text-placeholder',
            )}
            aria-hidden
          />
          <input
            value={currentQuery}
            placeholder={placeholder}
            onFocus={() => setIsOpen(true)}
            onChange={(event) => {
              setQuery(event.target.value);
              setIsOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && canCreate) {
                event.preventDefault();
                createOption();
              }
            }}
            className='min-w-0 flex-1 bg-transparent typo-b14 text-text-primary outline-none placeholder:text-text-placeholder'
          />
          {currentQuery ? (
            <BaseButton
              type='text'
              size='s'
              htmlType='button'
              aria-label='Clear search'
              onClick={() => setQuery('')}
              className='size-5 min-w-0 rounded-full border-0 p-0 bg-grayscale-gray02 text-text-placeholder hover:bg-grayscale-gray02 hover:text-text-placeholder'
              icon={<CancelIcon className='size-4' aria-hidden />}
            />
          ) : null}
        </div>

        {showLimitMessage ? (
          <p className='flex items-center gap-1 typo-b14Strong text-text-failed'>
            <InfoIcon className='size-5' aria-hidden />
            {limitMessage(maxSelections)}
          </p>
        ) : null}

        {showSuggestionPanel ? (
          <div className='rounded-[15px] border border-grayscale-gray01 bg-grayscale-gray00 p-3 shadow-3'>
            {suggestions.length > 0 ? (
              <div
                className={twMerge(
                  'flex flex-wrap items-start gap-2',
                  canCreate && 'pb-3',
                )}
              >
                {suggestions.map((option) => {
                  const selected = selectedValueSet.has(option.value);
                  return (
                    <SearchChip
                      key={option.value}
                      label={option.label}
                      selected={selected}
                      onClick={() =>
                        selected ? removeOption(option) : addOption(option)
                      }
                    />
                  );
                })}
              </div>
            ) : null}
            {canCreate ? (
              <div className='flex flex-wrap items-start gap-2'>
                <SearchChip
                  label={createLabel(currentQuery.trim())}
                  onClick={createOption}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {showDefaultSuggestionPanel ? (
        <div className='flex w-full flex-col gap-3 rounded-[8px] border-[1.6px] border-grayscale-gray02 bg-grayscale-gray00 p-3'>
          {defaultSuggestionTitle ? (
            <p className='w-full typo-b14Strong text-text-primary'>
              {defaultSuggestionTitle}
            </p>
          ) : null}
          <div className='flex flex-wrap items-start gap-3'>
            {normalizedOptions.slice(0, 8).map((option) => (
              <SearchChip
                key={option.value}
                label={option.label}
                onClick={() => addOption(option)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PathfinderMobileSearchSelect;
